'use client';

import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle, ArrowUpRight, ArrowDownRight, ArrowRight, Activity } from 'lucide-react';
import { useDefiLlamaData, formatDefiNumber, formatPercentChange, getTimeAgo } from '@/hooks/useDefiLlamaData';
import { useFirestoreCrypto } from '@/hooks/useFirestoreCrypto';
import { useMarketAnalysis } from '@/hooks/useMarketAnalysis';
import { getFearGreedColors } from '@/components/market/SemiCircleGauge';
import { getMajorCryptoImageUrl } from '@/lib/major-crypto-metadata';
import Tooltip from '@/components/ui/Tooltip';
import { getChain } from '@/lib/chains';

// TODO(B3): wire a per-chain selector so this terminal can show the active hub chain's
// DeFi metrics. `useDefiLlamaData()` currently returns Abstract data, so we pin the
// displayed chain to Abstract but resolve its LABEL from the registry (single source
// of truth) instead of hardcoding "Abstract".
const DISPLAY_CHAIN_ID = 'abstract';
const CHAIN_LABEL = getChain(DISPLAY_CHAIN_ID)?.label ?? 'Abstract';

// Shared box style — every single card/pill uses this exact padding.
// 0.5px hairline + 4px radius = dashboard-native (vs the old square hard border).
const BOX = 'p-3 lg:p-4 rounded bg-gray-50 dark:bg-surface border-[0.5px] border-gray-200 dark:border-divider hover:border-scout-gold/30 transition-all';

// ─── Sub-components ──────────────────────────────────────────────────

function StatCard({
  label,
  value,
  change,
  isLoading,
  tooltip,
}: {
  label: string;
  value: string;
  change?: number;
  isLoading?: boolean;
  tooltip?: string;
}) {
  const isPositive = change !== undefined && change >= 0;

  if (isLoading) {
    return (
      <div className={`${BOX} animate-pulse`}>
        <div className="h-3 bg-gray-100 dark:bg-surface2 rounded w-14 mb-1.5" />
        <div className="h-5 bg-gray-100 dark:bg-surface2 rounded w-16" />
      </div>
    );
  }

  const content = (
    <div className={BOX}>
      <span className="font-mono text-mono-label text-gray-500 dark:text-parchment-dim uppercase tracking-wider block mb-1">
        {label}
      </span>
      <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
        <span className="font-mono font-bold text-base text-gray-900 dark:text-parchment">{value}</span>
        {change !== undefined && (
          <span className={`font-mono text-mono-sm font-bold ${isPositive ? 'text-signal-green' : 'text-alert-red'}`}>
            {isPositive ? '↑' : '↓'}{formatPercentChange(Math.abs(change))}
          </span>
        )}
      </div>
    </div>
  );

  if (tooltip) {
    return <Tooltip content={tooltip} position="bottom">{content}</Tooltip>;
  }
  return content;
}

function FearGreedInline({
  label,
  value,
  isLoading,
  trend,
}: {
  label: string;
  value?: number;
  isLoading?: boolean;
  trend?: { direction: string; label: string; delta: number };
}) {
  if (isLoading || value === undefined) {
    return (
      <div className={`${BOX} animate-pulse`}>
        <div className="h-3 bg-gray-100 dark:bg-surface2 rounded w-full mb-2" />
        <div className="h-5 bg-gray-100 dark:bg-surface2 rounded w-20 mb-2" />
        <div className="h-1.5 bg-gray-100 dark:bg-surface2 rounded-full w-full" />
      </div>
    );
  }

  const clamped = Math.max(0, Math.min(100, value));
  const colors = getFearGreedColors(clamped);

  return (
    <div className={BOX}>
      <span className="font-mono text-mono-label text-gray-500 dark:text-parchment-dim uppercase tracking-wider block mb-1">
        {label}
      </span>
      <div className="flex items-center gap-1.5 mb-2">
        <span className={`font-mono text-base font-bold ${colors.text}`}>{clamped}</span>
        {trend?.direction === 'improving' && <ArrowUpRight size={12} className="text-signal-green" />}
        {trend?.direction === 'deteriorating' && <ArrowDownRight size={12} className="text-alert-red" />}
        {trend?.direction === 'stable' && <ArrowRight size={12} className="text-gray-500 dark:text-parchment-dim" />}
        <span className={`font-mono text-mono-sm ${colors.text}`}>{colors.label}</span>
      </div>
      <div className="relative h-1.5 py-1">
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1.5 rounded-full bg-neutral-800">
          <div
            className="absolute inset-0 rounded-full"
            style={{ background: 'linear-gradient(to right, #ef4444, #f97316, #eab308, #22c55e, #10b981)' }}
          />
        </div>
        <div
          className="absolute top-1/2 w-2.5 h-2.5 rounded-full border-2 border-white dark:border-ink transition-all duration-500"
          style={{
            left: `${clamped}%`,
            transform: 'translate(-50%, -50%)',
            backgroundColor: colors.color,
            boxShadow: `0 0 4px ${colors.color}40`,
          }}
        />
      </div>
    </div>
  );
}

function TopMoverPill({
  symbol,
  change,
  type,
}: {
  symbol: string;
  change: number;
  type: 'gainer' | 'loser';
}) {
  const isGainer = type === 'gainer';
  return (
    <div className={`${BOX} flex items-center gap-1.5 font-mono text-xs ${
      isGainer
        ? '!bg-signal-green/10 !border-signal-green/20'
        : '!bg-alert-red/10 !border-alert-red/20'
    }`}>
      <span className={`opacity-70 ${isGainer ? 'text-signal-green' : 'text-alert-red'}`}>{isGainer ? '▲' : '▼'}</span>
      <span className="font-semibold text-gray-900 dark:text-parchment">{symbol}</span>
      <span className={isGainer ? 'text-signal-green' : 'text-alert-red'}>{isGainer ? '+' : ''}{change.toFixed(1)}%</span>
    </div>
  );
}

function MarketBreadth({ gainers, losers }: { gainers: number; losers: number }) {
  const total = gainers + losers;
  const gainerPercent = total > 0 ? (gainers / total) * 100 : 50;

  return (
    <Tooltip content={`${gainers} tokens up, ${losers} tokens down in 24h`} position="bottom">
      <div className={`${BOX} flex items-center gap-2 cursor-help`}>
        <div className="flex items-center gap-1">
          <span className="text-signal-green font-mono text-xs font-bold">{gainers}</span>
          <span className="text-gray-500 dark:text-parchment-dim">↑</span>
        </div>
        <div className="w-12 lg:w-16 h-1.5 bg-alert-red/30 rounded-full overflow-hidden hidden sm:block">
          <div
            className="h-full bg-signal-green rounded-full transition-all duration-500"
            style={{ width: `${gainerPercent}%` }}
          />
        </div>
        <div className="flex items-center gap-1">
          <span className="text-gray-500 dark:text-parchment-dim">↓</span>
          <span className="text-alert-red font-mono text-xs font-bold">{losers}</span>
        </div>
      </div>
    </Tooltip>
  );
}

function CryptoPriceCompact({
  symbol,
  price,
  dominance,
  isLoading,
}: {
  symbol: 'BTC' | 'ETH';
  price?: number;
  dominance?: number;
  isLoading?: boolean;
}) {
  if (isLoading || !price) {
    return (
      <div className={`${BOX} flex items-center gap-2 animate-pulse`}>
        <div className="w-5 h-5 bg-gray-100 dark:bg-surface2 rounded-full" />
        <div className="h-4 bg-gray-100 dark:bg-surface2 rounded w-12" />
      </div>
    );
  }

  const formatPrice = (p: number) => p >= 1000 ? `$${(p / 1000).toFixed(1)}k` : `$${p.toFixed(0)}`;

  return (
    <Tooltip content={`${symbol} dominance: ${dominance?.toFixed(1) || '—'}%`} position="bottom">
      <div className={`${BOX} flex items-center gap-2 cursor-help`}>
        <img
          src={getMajorCryptoImageUrl(symbol) || ''}
          alt={symbol}
          className="w-5 h-5 rounded-full shrink-0"
        />
        <span className="font-mono text-sm font-semibold text-gray-900 dark:text-parchment">{formatPrice(price)}</span>
        {dominance && (
          <span className="font-mono text-mono-sm text-gray-500 dark:text-parchment-dim hidden sm:inline">
            {dominance.toFixed(1)}%
          </span>
        )}
      </div>
    </Tooltip>
  );
}

function GlobalFearGreedCompact({ value, isLoading }: { value?: number; isLoading?: boolean }) {
  if (isLoading || value === undefined) {
    return (
      <div className={`${BOX} flex items-center gap-2 animate-pulse`}>
        <div className="w-5 h-5 bg-gray-100 dark:bg-surface2 rounded-full" />
        <div className="h-4 bg-gray-100 dark:bg-surface2 rounded w-8" />
      </div>
    );
  }

  const colors = getFearGreedColors(value);

  return (
    <Tooltip content={`Global Crypto Fear & Greed: ${colors.label}`} position="bottom">
      <div className={`${BOX} flex items-center gap-2 cursor-help`}>
        <div className="relative shrink-0 w-5 h-5">
          <svg width={20} height={20} className="transform -rotate-90">
            <circle cx={10} cy={10} r={8} fill="none" className="stroke-gray-200 dark:stroke-surface2" strokeWidth={2.5} />
            <circle cx={10} cy={10} r={8} fill="none" stroke={colors.color} strokeWidth={2.5} strokeLinecap="round"
              strokeDasharray={50.27} strokeDashoffset={50.27 - (value / 100) * 50.27} className="transition-all duration-700" />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`font-mono text-[7px] font-bold ${colors.text}`}>{value}</span>
          </div>
        </div>
        <span className="font-mono text-mono-sm text-gray-500 dark:text-parchment-dim">F&G</span>
      </div>
    </Tooltip>
  );
}

// ─── Main Component ──────────────────────────────────────────────────

interface MarketStatsBoxesProps {
  className?: string;
  /** When embedded in the dashboard shell, drop the internal max-w/padding container
      so the stats bar inherits the shell's content container (no double gutter). */
  bare?: boolean;
  tokenCount?: number;
  nftCount?: number;
  topGainer?: { symbol: string; priceChange24h?: number } | null;
  topLoser?: { symbol: string; priceChange24h?: number } | null;
  tokenGainers?: number;
  tokenLosers?: number;
  activeTab?: 'tokens' | 'nfts';
}

export default function MarketStatsBoxes({
  className = '',
  bare = false,
  tokenCount = 0,
  nftCount = 0,
  topGainer,
  topLoser,
  tokenGainers = 0,
  tokenLosers = 0,
}: MarketStatsBoxesProps) {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const { data, loading, error, isStale } = useDefiLlamaData();
  const { data: cryptoData, loading: cryptoLoading } = useFirestoreCrypto({ enableRealTimeUpdates: true });
  const { pulse: marketPulse } = useMarketAnalysis();

  const btc = cryptoData?.bitcoin;
  const eth = cryptoData?.ethereum;
  const fearGreed = marketPulse?.globalContext?.fearGreed;
  const globalMarket = marketPulse?.globalContext?.globalMarket;
  const abstractFearGreed = marketPulse?.abstractFearGreed;

  if (!hasMounted) return null;
  if ((error && !data) || (!loading && data && data.tvl === 0)) return null;

  // ── Embedded (dashboard) mode ──────────────────────────────────────────────
  // Dissolve the boxed "TERMINAL" frame: no outer card, no competing <h1>/Activity
  // icon (the route already renders the "<Chain> Market" heading), and NO global
  // BTC/ETH/F&G row (that global ticker delayed the chain's own subject). Just the
  // chain's living KPI strip + its movers, in dashboard tokens.
  if (bare) {
    return (
      <div className={className}>
        <div className="py-4">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6 lg:gap-3">
            <div className="col-span-2 sm:col-span-3 lg:col-span-2">
              <FearGreedInline
                label={`${CHAIN_LABEL} F&G`}
                value={abstractFearGreed?.value}
                isLoading={loading}
                trend={abstractFearGreed?.trend}
              />
            </div>
            <StatCard label="24H Vol" value={formatDefiNumber(data?.dexVolume24h || 0)} change={data?.dexVolumeChange24h} isLoading={loading} tooltip={`DEX trading volume on ${CHAIN_LABEL}`} />
            <StatCard label="Stables" value={formatDefiNumber(data?.stablecoinMcap || 0)} isLoading={loading} tooltip={`Stablecoin market cap on ${CHAIN_LABEL}`} />
            <StatCard label="24H Fees" value={formatDefiNumber(data?.fees24h || 0)} change={data?.feesChange24h} isLoading={loading} tooltip="Protocol fees in last 24 hours" />
            <StatCard label="Protocols" value={String(data?.protocolCount || '—')} isLoading={loading} tooltip={`Active protocols on ${CHAIN_LABEL}`} />
          </div>
          {(topGainer || topLoser || tokenGainers > 0 || tokenLosers > 0) && (
            <div className="mt-2 flex flex-wrap items-stretch gap-2">
              {topGainer && (
                <TopMoverPill symbol={topGainer.symbol} change={topGainer.priceChange24h || 0} type="gainer" />
              )}
              {topLoser && (
                <TopMoverPill symbol={topLoser.symbol} change={topLoser.priceChange24h || 0} type="loser" />
              )}
              <MarketBreadth gainers={tokenGainers} losers={tokenLosers} />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      <div className={bare ? 'py-4 lg:py-5' : 'max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-5'}>
        <div className="bg-white dark:bg-ink border border-gray-200 dark:border-divider p-4 lg:p-5 space-y-4">

          {/* Row 1: Header + movers — all items same height */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-scout-gold" />
                <h1 className="font-mono text-base lg:text-lg font-bold text-gray-900 dark:text-parchment uppercase tracking-tight">
                  {CHAIN_LABEL} Terminal
                </h1>
              </div>
              <div className="w-px h-4 bg-gray-200 dark:bg-divider" />
              <span className="font-mono text-mono-sm text-gray-500 dark:text-parchment-dim">
                {tokenCount} Tokens · {nftCount} NFTs
              </span>
              {data?.timestamp && (
                <>
                  <div className="w-px h-4 bg-gray-200 dark:bg-divider hidden sm:block" />
                  <span className="font-mono text-mono-sm text-gray-500 dark:text-parchment-dim hidden sm:flex items-center gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-signal-green animate-pulse" />
                    {getTimeAgo(data.timestamp)}
                    {isStale && <AlertTriangle size={10} className="text-scout-gold" />}
                  </span>
                </>
              )}
            </div>

            <div className="flex items-stretch gap-2">
              {topGainer && (
                <TopMoverPill symbol={topGainer.symbol} change={topGainer.priceChange24h || 0} type="gainer" />
              )}
              {topLoser && (
                <TopMoverPill symbol={topLoser.symbol} change={topLoser.priceChange24h || 0} type="loser" />
              )}
              <MarketBreadth gainers={tokenGainers} losers={tokenLosers} />
            </div>
          </div>

          {/* Row 2: F&G bar + ecosystem stats — uniform grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 lg:gap-3">
            <div className="col-span-2 sm:col-span-3 lg:col-span-2">
              <FearGreedInline
                label={`${CHAIN_LABEL} F&G`}
                value={abstractFearGreed?.value}
                isLoading={loading}
                trend={abstractFearGreed?.trend}
              />
            </div>
            <StatCard label="24H Vol" value={formatDefiNumber(data?.dexVolume24h || 0)} change={data?.dexVolumeChange24h} isLoading={loading} tooltip={`DEX trading volume on ${CHAIN_LABEL}`} />
            <StatCard label="Stables" value={formatDefiNumber(data?.stablecoinMcap || 0)} isLoading={loading} tooltip={`Stablecoin market cap on ${CHAIN_LABEL}`} />
            <StatCard label="24H Fees" value={formatDefiNumber(data?.fees24h || 0)} change={data?.feesChange24h} isLoading={loading} tooltip="Protocol fees in last 24 hours" />
            <StatCard label="Protocols" value={String(data?.protocolCount || '—')} isLoading={loading} tooltip={`Active protocols on ${CHAIN_LABEL}`} />
          </div>

          {/* Row 3: Global context — uniform items */}
          <div className="flex flex-wrap items-stretch gap-2 gap-y-2 pt-2 border-t border-gray-200 dark:border-divider">
            <span className="font-mono text-mono-label text-gray-500 dark:text-parchment-dim uppercase tracking-wider shrink-0 flex items-center">Global</span>
            <CryptoPriceCompact symbol="BTC" price={btc?.currentPrice} dominance={globalMarket?.btcDominance} isLoading={cryptoLoading} />
            <CryptoPriceCompact symbol="ETH" price={eth?.currentPrice} dominance={globalMarket?.ethDominance} isLoading={cryptoLoading} />
            <GlobalFearGreedCompact value={fearGreed?.value} isLoading={cryptoLoading} />
          </div>
        </div>
      </div>
    </div>
  );
}
